import { ActionIconButton, TableColumnHeader } from "@/components/shared/table/Table";
import { User } from "@/graphql/__generated__/graphql";
import useUserStore from "@/store/shield";
import { isManager } from "@/utils/authentication";
import { toNormalCase } from "@/utils/helpers";
import { ColumnDef } from "@tanstack/react-table";
import { TFunction } from "i18next";
import { MailIcon, TrashIcon } from "lucide-react";
import { IntlShape } from "react-intl";

export const getColumns = (intl: IntlShape, t: TFunction, actionSendInviteFn?: any, actionRemoveFn?: any, isProcessing?: boolean): ColumnDef<User, string>[] => {
  const { userId } = useUserStore();

  return [
    {
      accessorKey: "firstname",
      header: ({ column }) => <TableColumnHeader hideSortingButton={true} text={t("tables:users.columns.firstname")} column={column} />,
      cell: ({ row }) => row.original.firstname,
    },
    {
      accessorKey: "lastname",
      header: ({ column }) => <TableColumnHeader hideSortingButton={true} text={t("tables:users.columns.lastname")} column={column} />,
      cell: ({ row }) => row.original.lastname,
    },
    {
      accessorKey: "email",
      header: ({ column }) => <TableColumnHeader hideSortingButton={true} text={t("tables:users.columns.email")} column={column} />,
      cell: ({ row }) => row.original.email,
    },
    {
      accessorKey: "organization.name",
      header: ({ column }) => <TableColumnHeader hideSortingButton={true} text={t("tables:users.columns.organization")} column={column} />,
      cell: ({ row }) => row.original.organization.name,
    },
    {
      accessorKey: "role",
      header: ({ column }) => <TableColumnHeader hideSortingButton={true} text={t("tables:users.columns.role")} column={column} />,
      cell: ({ row }) => toNormalCase(row.original.role),
    },
    isManager() ? {
      id: "actions",
      cell: ({ row }) => {
        const canSendInvite = userId !== row.original.id && !row.original.emailVerified;
        const canRemove = userId !== row.original.id;

        if (!canSendInvite && !canRemove) return null;

        return (
          <div className="flex gap-2 justify-end">
            {canSendInvite && (
              <ActionIconButton
                dataObj={row.original}
                fn={actionSendInviteFn}
                tooltipText={t("common:buttons.sendinvite")}
                disabled={isProcessing}
              >
                <MailIcon className="h-4 w-4" />
              </ActionIconButton>
            )}

            {canRemove && (
              <ActionIconButton
                dataObj={row.original}
                fn={actionRemoveFn}
                tooltipText={t("common:buttons.remove")}
                variant="destructive"
                disabled={isProcessing}
              >
                <TrashIcon className="h-4 w-4" />
              </ActionIconButton>
            )}
          </div>
        );
      },
    } : undefined,
  ].filter(Boolean) as ColumnDef<User, string>[];
};
